import {
    EventAction,
    EventType,
    KeyCursor,
    LicenseEvent,
    LicenseEventExportNotificationData,
    LicenseEventQuery,
    LicenseEventResult,
} from "domain/licenseEvent";
import { apiGatewayService, ApiType } from "services/api/ApiGatewayService";

const PATH = "/api/license-events/query";

export type LicenseEventExportResult = { type: "completed"; blob: Blob } | { type: "queued"; jobUuid: string };

interface LicenseEventQueryDto {
    search: string;
    from: string;
    to: string;
    product_ids: string[];
    event_types: string[];
    event_actions: string[];
    event_source: string[];
    cursor?: LicenseEventCursorDto;
}

interface LicenseEventDto {
    event_uuid: string;
    username: string;
    pool_name: string;
    time: string;
    type: EventType;
    product_id: string;
    amount_before: number;
    amount_after: number;
    change: number;
    expiration_after: string;
    action: EventAction;
    notes?: string;
    license_consumption_id?: string;
    asset_id?: string;
    targeted_tenant: string;
    used_amount?: number;
    event_source: string;
    product_name: string;
    product_version: string;
    extra: string;
    assignment_mode?: string;
}
export interface LicenseEventCursorDto {
    event_uuid: string;
    tenant_uuid: string;
    time: string;
}

interface LicenseEventsResponseDto {
    license_history: LicenseEventDto[];
    cursor: LicenseEventCursorDto;
}

interface LicenseEventsCountResponseDto {
    total_events: number;
    accurate: boolean;
}

interface LicenseEventsCountResponse {
    totalEvents: number;
    accurate: boolean;
}

interface LicenseEventExportJobResponseDto {
    job_uuid: string;
}

interface LicenseEventExportDataDto {
    job_uuid: string;
    download_url?: string;
    expiration?: number;
    error?: string;
}

export function parseLicenseEventExportData(notificationData: string): LicenseEventExportNotificationData | undefined {
    try {
        const dto: LicenseEventExportDataDto = JSON.parse(notificationData);
        return {
            jobUuid: dto.job_uuid,
            downloadUrl: dto.download_url,
            expirationSeconds: dto.expiration,
            error: dto.error,
        };
    } catch {
        return undefined;
    }
}

function decodeBase64ToBlob(base64: string): Blob {
    const cleanBase64 = base64.replace(/\s/g, "");
    const byteCharacters = atob(cleanBase64);
    const byteNumbers = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Blob([byteNumbers], { type: "application/zip" });
}

function toLicenseEvent(data: LicenseEventDto): LicenseEvent {
    return {
        eventUuid: data.event_uuid,
        productId: data.product_id,
        eventType: data.type,
        change: data.change,
        remaining: data.amount_after,
        eventDate: data.time,
        user: data.username,
        poolName: data.pool_name,
        expirationDate: data.expiration_after,
        notes: data.notes,
        eventAction: data.action,
        eventSource: data.event_source,
        licenseConsumptionId: data.license_consumption_id,
        assetId: data.asset_id,
        targetedTenant: data.targeted_tenant,
        amountBefore: data.amount_before,
        amountAfter: data.amount_after,
        usedAmount: data.used_amount,
        productName: data.product_name,
        productVersion: data.product_version,
        extra: data.extra,
        assignmentMode: data.assignment_mode,
    };
}
function toLicenseCursor(dto: LicenseEventCursorDto): KeyCursor | undefined {
    return dto == null
        ? undefined
        : {
              eventUuid: dto.event_uuid,
              tenantUuid: dto.tenant_uuid,
              time: dto.time,
          };
}
function toQueryDto(data?: LicenseEventQuery): LicenseEventQueryDto | undefined {
    if (typeof data === "undefined") {
        return undefined;
    }
    return {
        search: data.search,
        from: data.from,
        to: data.to,
        product_ids: data.productIds,
        event_types: data.actions,
        event_actions: data.eventActions,
        event_source: data.eventSources,
        cursor: toQueryCursorDto(data.cursor),
    };
}

function toQueryCursorDto(data?: KeyCursor): LicenseEventCursorDto | undefined {
    if (typeof data === "undefined") {
        return undefined;
    }
    return {
        event_uuid: data.eventUuid,
        tenant_uuid: data.tenantUuid,
        time: data.time,
    };
}

class LicenseEventService {
    public fetchLicenseEvents(
        query: LicenseEventQuery,
        abortController?: AbortController
    ): Promise<LicenseEventResult> {
        return apiGatewayService
            .invokeApi(PATH, "POST", toQueryDto(query), {
                abortSignal: abortController?.signal,
                apiType: ApiType.LAUREL,
            })
            .then((data: LicenseEventsResponseDto) => {
                const licenseEvents = data.license_history.map((each) => toLicenseEvent(each));
                const cursor = toLicenseCursor(data.cursor);
                return { licenseEvents: licenseEvents, cursor: cursor };
            });
    }

    public fetchEventsCount(
        query: LicenseEventQuery,
        abortController?: AbortController
    ): Promise<LicenseEventsCountResponse> {
        return apiGatewayService
            .invokeApi(PATH + "?count=true", "POST", toQueryDto(query), {
                abortSignal: abortController?.signal,
                apiType: ApiType.LAUREL,
            })
            .then((data: LicenseEventsCountResponseDto) => {
                return {
                    totalEvents: data.total_events,
                    accurate: data.accurate,
                };
            });
    }

    public exportLicenseEvents(
        query: LicenseEventQuery,
        abortController: AbortController
    ): Promise<LicenseEventExportResult> {
        return apiGatewayService
            .invokeApi(PATH + "?format=csv", "POST", toQueryDto(query), {
                apiType: ApiType.LAUREL,
                jsonResponse: false,
                abortSignal: abortController.signal,
            })
            .then((response: Response) => {
                if (response.status === 202) {
                    return response.json().then((dto: LicenseEventExportJobResponseDto) => ({
                        type: "queued" as const,
                        jobUuid: dto.job_uuid,
                    }));
                }
                return response.text().then((text) => ({
                    type: "completed" as const,
                    blob: decodeBase64ToBlob(text),
                }));
            });
    }
}

export const licenseEventsService = new LicenseEventService();
